import { ActionMode } from '@/enums/scenario-builder.enum'
import {
  TBenchmark,
  TPortfolio,
  TReportTemplate,
  TUserInfo,
} from '@/interfaces/scenarioBuilder.interface'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'

interface ScenarioBuilderState {
  isCreateReportModalOpened: boolean
  isReloadingReportTemplateGrid: boolean
  actionMode: ActionMode
  selectedReportTemplate?: TReportTemplate
  isDeleteReportTemplateModalOpened: boolean
  isRunReportTemplateModalOpened: boolean
  isKeyRatesToggleConfirmationModalOpened: boolean
  shockTypeSwitchChange: (() => void) | null
  portfolioList: TPortfolio[]
  benchmarkList: TBenchmark[]
  // When each list was last fetched (epoch ms). Persisted along with the
  // lists themselves (via redux-persist), so cache freshness survives page
  // reloads too. See PORTFOLIO_BENCHMARK_CACHE_TTL_MS in common.constant.ts.
  portfolioListFetchedAt: number | null
  benchmarkListFetchedAt: number | null
  hasNewModifiedValues: boolean
  portfolioListLoading: boolean
  benchmarkListLoading: boolean
  isSaveButtonDisabled: boolean
  userInfo: TUserInfo
  isLoading: boolean
}

const initialState: ScenarioBuilderState = {
  isCreateReportModalOpened: false,
  isReloadingReportTemplateGrid: false,
  actionMode: ActionMode.VIEW,
  isDeleteReportTemplateModalOpened: false,
  isRunReportTemplateModalOpened: false,
  isKeyRatesToggleConfirmationModalOpened: false,
  shockTypeSwitchChange: null,
  portfolioList: [],
  benchmarkList: [],
  portfolioListFetchedAt: null,
  benchmarkListFetchedAt: null,
  hasNewModifiedValues: false,
  portfolioListLoading: false,
  benchmarkListLoading: false,
  isSaveButtonDisabled: false,
  selectedReportTemplate: {} as TReportTemplate,
  userInfo: {
    name: '',
    id: 0,
    permissions: [],
  },
  isLoading: true,
}

export const scenarioBuilderSlice = createSlice({
  name: 'scenarioBuilder',
  initialState,
  reducers: {
    toggleCreateReportModalOpened: (state, action: PayloadAction<boolean>) => {
      state.isCreateReportModalOpened = action.payload
    },
    toggleIsReloadingReportTemplateGrid: (state) => {
      state.isReloadingReportTemplateGrid = !state.isReloadingReportTemplateGrid
    },
    setActionMode: (state, action: PayloadAction<ActionMode>) => {
      state.actionMode = action.payload
    },
    setSelectedReportTemplate: (
      state,
      action: PayloadAction<TReportTemplate | undefined>,
    ) => {
      state.selectedReportTemplate = action.payload
    },
    toggleDeleteReportTemplateModalOpened: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isDeleteReportTemplateModalOpened = action.payload
    },

    toggleRunReportTemplateModalOpened: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isRunReportTemplateModalOpened = action.payload
    },
    setKeyRatesToggleConfirmationModalOpened: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isKeyRatesToggleConfirmationModalOpened = action.payload
    },
    setShockTypeSwitchChange: (
      state,
      action: PayloadAction<(() => void) | null>,
    ) => {
      state.shockTypeSwitchChange = action.payload
    },
    setPortfolioList: (state, action: PayloadAction<TPortfolio[]>) => {
      state.portfolioList = action.payload
      state.portfolioListFetchedAt = Date.now()
    },
    setBenchmarkList: (state, action: PayloadAction<TBenchmark[]>) => {
      state.benchmarkList = action.payload
      state.benchmarkListFetchedAt = Date.now()
    },
    setHasNewModifiedValues: (state, action: PayloadAction<boolean>) => {
      state.hasNewModifiedValues = action.payload
    },
    setPortfolioListLoading: (state, action: PayloadAction<boolean>) => {
      state.portfolioListLoading = action.payload
    },
    setBenchmarkListLoading: (state, action: PayloadAction<boolean>) => {
      state.benchmarkListLoading = action.payload
    },
    setSaveButtonDisabled: (state, action: PayloadAction<boolean>) => {
      state.isSaveButtonDisabled = action.payload
    },
    setUserInfo: (state, action: PayloadAction<TUserInfo>) => {
      state.userInfo = action.payload
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    clearState: (state) => {
      state.selectedReportTemplate = undefined
      state.actionMode = 1
    },
  },
})

export const {
  toggleCreateReportModalOpened,
  toggleIsReloadingReportTemplateGrid,
  setActionMode,
  setSelectedReportTemplate,
  toggleDeleteReportTemplateModalOpened,
  toggleRunReportTemplateModalOpened,
  setKeyRatesToggleConfirmationModalOpened,
  setShockTypeSwitchChange,
  setPortfolioList,
  setBenchmarkList,
  setHasNewModifiedValues,
  setPortfolioListLoading,
  setBenchmarkListLoading,
  setSaveButtonDisabled,
  setUserInfo,
  setIsLoading,
  clearState,
} = scenarioBuilderSlice.actions

export default scenarioBuilderSlice.reducer
